import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Send, Image, Type, Clock, CheckCircle, XCircle, Loader2, RefreshCw, Plus, X, Smartphone } from "lucide-react";
import { toast } from "sonner";

const API = '/api';

const STATUS_COLORS = [
  { value: "#075E54", label: "Verde WA" },
  { value: "#128C7E", label: "Teal" },
  { value: "#25D366", label: "Verde Claro" },
  { value: "#1B74E4", label: "Azul" },
  { value: "#7C3AED", label: "Violeta" },
  { value: "#DC2626", label: "Rojo" },
  { value: "#D97706", label: "Naranja" },
  { value: "#111827", label: "Negro" },
];

const statusBadge = (status) => {
  const map = {
    pending: { cls: "bg-yellow-500/15 text-yellow-400 border-yellow-500/20", label: "Pendiente" },
    in_progress: { cls: "bg-blue-500/15 text-blue-400 border-blue-500/20", label: "En Proceso" },
    completed: { cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20", label: "Publicado" },
    failed: { cls: "bg-red-500/15 text-red-400 border-red-500/20", label: "Fallido" },
  };
  const info = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold border ${info.cls}`}>
      {info.label}
    </span>
  );
};

const statusIcon = (status) => {
  switch (status) {
    case "completed": return <CheckCircle className="w-4 h-4 text-emerald-400" />;
    case "failed": return <XCircle className="w-4 h-4 text-red-400" />;
    case "in_progress": return <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />;
    default: return <Clock className="w-4 h-4 text-yellow-400" />;
  }
};

export default function WhatsAppStatus() {
  const [history, setHistory] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filterProfile, setFilterProfile] = useState("");
  const [form, setForm] = useState({
    profile_id: "",
    type: "text",
    text: "",
    image_url: "",
    background_color: "#075E54",
  });

  const fetchHistory = useCallback(async () => {
    try {
      const params = {};
      if (filterProfile) params.profile_id = filterProfile;
      const res = await axios.get(`${API}/whatsapp/status/history`, { params });
      setHistory(res.data);
    } catch (e) {
      // error silenced
    } finally {
      setLoading(false);
    }
  }, [filterProfile]);

  const fetchProfiles = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/profiles`);
      setProfiles(res.data.filter(p => p.type === "android"));
    } catch (e) {
      // error silenced
    }
  }, []);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);
  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // Poll every 15s for status updates
  useEffect(() => {
    const interval = setInterval(fetchHistory, 15000);
    return () => clearInterval(interval);
  }, [fetchHistory]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.profile_id) return toast.error("Selecciona un perfil Android");
    if (form.type === "text" && !form.text?.trim()) return toast.error("Escribe el texto del estado");
    if (form.type === "image" && !form.image_url?.trim()) return toast.error("Ingresa la URL de la imagen");

    setSubmitting(true);
    try {
      const payload = {
        profile_id: form.profile_id,
        type: form.type,
        text: form.text || null,
        image_url: form.type === "image" ? form.image_url : null,
        background_color: form.type === "text" ? form.background_color : null,
      };
      await axios.post(`${API}/whatsapp/status/create`, payload);
      toast.success("Estado creado. Se publicara cuando la app Android lo procese.");
      setShowCreate(false);
      setForm({ profile_id: "", type: "text", text: "", image_url: "", background_color: "#075E54" });
      fetchHistory();
    } catch (e) {
      toast.error("Error al crear estado");
    } finally {
      setSubmitting(false);
    }
  };

  const getProfileName = (profileId) => {
    const p = profiles.find(pr => pr.id === profileId);
    return p ? p.name : profileId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="wa-status-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-white uppercase">
            Estados WhatsApp
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Publica estados de texto o imagen en WhatsApp
          </p>
        </div>
        <div className="flex gap-2 self-start">
          <button
            data-testid="refresh-status-btn"
            onClick={() => { setLoading(true); fetchHistory(); }}
            className="flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-200 rounded-lg px-3 py-2 text-sm font-medium transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            data-testid="create-status-btn"
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-500 text-white rounded-lg px-4 py-2 text-sm font-semibold transition-colors"
          >
            <Plus className="w-4 h-4" />
            Nuevo Estado
          </button>
        </div>
      </div>

      {/* Create Status Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[200] p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-zinc-900 border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <h3 className="text-white font-bold text-base">Crear Estado de WhatsApp</h3>
              <button data-testid="close-status-modal" onClick={() => setShowCreate(false)} className="text-zinc-400 hover:text-white p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="px-5 pb-5 space-y-4">
              {/* Profile */}
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">Perfil Android</label>
                <select
                  data-testid="status-profile-select"
                  value={form.profile_id}
                  onChange={e => setForm(f => ({ ...f, profile_id: e.target.value }))}
                  className="w-full bg-zinc-950/50 border border-zinc-800 focus:border-green-500 rounded-md px-3 py-2 text-sm text-white outline-none transition-colors"
                >
                  <option value="">Seleccionar perfil...</option>
                  {profiles.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              {/* Type toggle */}
              <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">Tipo de Estado</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    data-testid="type-text-btn"
                    onClick={() => setForm(f => ({ ...f, type: "text" }))}
                    className={`flex-1 flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors border ${
                      form.type === "text"
                        ? "bg-green-600/15 text-green-400 border-green-500/30"
                        : "bg-zinc-950/50 text-zinc-400 border-zinc-800 hover:border-zinc-600"
                    }`}
                  >
                    <Type className="w-4 h-4" />
                    Texto
                  </button>
                  <button
                    type="button"
                    data-testid="type-image-btn"
                    onClick={() => setForm(f => ({ ...f, type: "image" }))}
                    className={`flex-1 flex items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors border ${
                      form.type === "image"
                        ? "bg-green-600/15 text-green-400 border-green-500/30"
                        : "bg-zinc-950/50 text-zinc-400 border-zinc-800 hover:border-zinc-600"
                    }`}
                  >
                    <Image className="w-4 h-4" />
                    Imagen
                  </button>
                </div>
              </div>

              {/* Text input */}
              {form.type === "text" && (
                <>
                  <div>
                    <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">Texto del Estado</label>
                    <textarea
                      data-testid="status-text-input"
                      value={form.text}
                      onChange={e => setForm(f => ({ ...f, text: e.target.value }))}
                      rows={3}
                      maxLength={700}
                      placeholder="Escribe el texto de tu estado..."
                      className="w-full bg-zinc-950/50 border border-zinc-800 focus:border-green-500 focus:ring-1 focus:ring-green-500/20 rounded-md px-3 py-2 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors resize-none"
                    />
                    <p className="text-[10px] text-zinc-600 mt-1 text-right">{form.text.length}/700</p>
                  </div>

                  {/* Background color */}
                  <div>
                    <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">Color de Fondo</label>
                    <div className="flex flex-wrap gap-2">
                      {STATUS_COLORS.map(c => (
                        <button
                          key={c.value}
                          type="button"
                          data-testid={`color-${c.value}`}
                          onClick={() => setForm(f => ({ ...f, background_color: c.value }))}
                          className={`w-8 h-8 rounded-full border-2 transition-all ${
                            form.background_color === c.value
                              ? "border-white scale-110"
                              : "border-transparent hover:border-zinc-600"
                          }`}
                          style={{ backgroundColor: c.value }}
                          title={c.label}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Preview */}
                  <div>
                    <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">Vista Previa</label>
                    <div
                      className="rounded-xl p-6 min-h-[120px] flex items-center justify-center text-center"
                      style={{ backgroundColor: form.background_color }}
                    >
                      <p className="text-white font-medium text-sm leading-relaxed max-w-[280px]">
                        {form.text || "Tu estado aparecera aqui..."}
                      </p>
                    </div>
                  </div>
                </>
              )}

              {/* Image URL input */}
              {form.type === "image" && (
                <>
                  <div>
                    <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">URL de la Imagen</label>
                    <input
                      data-testid="status-image-url-input"
                      value={form.image_url}
                      onChange={e => setForm(f => ({ ...f, image_url: e.target.value }))}
                      placeholder="https://... (URL publica de la imagen)"
                      className="w-full bg-zinc-950/50 border border-zinc-800 focus:border-green-500 focus:ring-1 focus:ring-green-500/20 rounded-md px-3 py-2 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors"
                    />
                  </div>
                  {form.text !== undefined && (
                    <div>
                      <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">Caption (Opcional)</label>
                      <input
                        data-testid="status-caption-input"
                        value={form.text}
                        onChange={e => setForm(f => ({ ...f, text: e.target.value }))}
                        placeholder="Texto que acompana la imagen..."
                        className="w-full bg-zinc-950/50 border border-zinc-800 focus:border-green-500 focus:ring-1 focus:ring-green-500/20 rounded-md px-3 py-2 text-sm text-white placeholder:text-zinc-600 outline-none transition-colors"
                      />
                    </div>
                  )}
                  {/* Image preview */}
                  {form.image_url && (
                    <div>
                      <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block mb-1.5">Vista Previa</label>
                      <div className="bg-zinc-950/50 border border-zinc-800 rounded-xl overflow-hidden">
                        <img
                          src={form.image_url}
                          alt="Preview"
                          className="w-full max-h-48 object-contain"
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* Submit */}
              <button
                type="submit"
                data-testid="submit-status-btn"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {submitting ? "Creando..." : "Publicar Estado"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-3">
        <select
          data-testid="filter-status-profile"
          value={filterProfile}
          onChange={e => setFilterProfile(e.target.value)}
          className="bg-zinc-950/50 border border-zinc-800 focus:border-green-500 rounded-md px-3 py-2 text-sm text-white outline-none transition-colors min-w-[180px]"
        >
          <option value="">Todos los perfiles</option>
          {profiles.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-zinc-900 border border-white/5 rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Total</p>
          <p className="text-2xl font-bold text-white mt-1">{history.length}</p>
        </div>
        <div className="bg-zinc-900 border border-white/5 rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Pendientes</p>
          <p className="text-2xl font-bold text-yellow-400 mt-1">{history.filter(h => h.status === "pending").length}</p>
        </div>
        <div className="bg-zinc-900 border border-white/5 rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Publicados</p>
          <p className="text-2xl font-bold text-emerald-400 mt-1">{history.filter(h => h.status === "completed").length}</p>
        </div>
        <div className="bg-zinc-900 border border-white/5 rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Fallidos</p>
          <p className="text-2xl font-bold text-red-400 mt-1">{history.filter(h => h.status === "failed").length}</p>
        </div>
      </div>

      {/* History */}
      <div className="bg-zinc-900 border border-white/5 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-white/5">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Historial de Estados</p>
        </div>

        {history.length === 0 ? (
          <div className="text-center py-16">
            <Send className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500 text-sm">No hay estados publicados</p>
            <p className="text-zinc-600 text-xs mt-1">Crea un nuevo estado para empezar</p>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {history.map((item, i) => (
              <div key={item.id || i} data-testid={`status-row-${i}`} className="flex items-center gap-4 px-4 py-3 hover:bg-white/[0.02] transition-colors">
                {/* Icon */}
                <div className="flex-shrink-0">
                  {statusIcon(item.status)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    {item.type === "text" ? (
                      <Type className="w-3.5 h-3.5 text-zinc-500" />
                    ) : (
                      <Image className="w-3.5 h-3.5 text-zinc-500" />
                    )}
                    <span className="text-xs text-zinc-400 uppercase font-medium">{item.type === "text" ? "Texto" : "Imagen"}</span>
                    {statusBadge(item.status)}
                  </div>
                  <p className="text-sm text-zinc-200 truncate">
                    {item.text || item.image_url || "Sin contenido"}
                  </p>
                  {item.error && (
                    <p className="text-[10px] text-red-400 mt-0.5 truncate">Error: {item.error}</p>
                  )}
                </div>

                {/* Meta */}
                <div className="flex-shrink-0 text-right">
                  <div className="flex items-center gap-1 text-[10px] text-zinc-500 mb-0.5">
                    <Smartphone className="w-3 h-3" />
                    {getProfileName(item.profile_id)}
                  </div>
                  <p className="text-[10px] text-zinc-600">
                    {item.created_at ? new Date(item.created_at).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
